'use client';

import React, { useState, useCallback, useEffect, useRef } from 'react';
import Link from 'next/link';
import { Trash2, Send, PackageSearch, Heart, CheckCircle2, X, ClipboardList, ChevronRight, Check, Building2, Calendar, Mail, Plus } from 'lucide-react';
import { clsx } from 'clsx';
import { ArticleNameBlock, Spinner, ProductImage } from '@paeffgen/react/crm-documents/ui';
import {
  formatDate,
  formatRequestPackageBreakdown,
  formatQuantity,
  getRequestAnzahl,
  useDebouncedInput,
} from '@paeffgen/react/crm-documents/util';
import { useCart } from './cart-provider';
import { DeliveryDatePicker, type DeliveryValue } from './delivery-date-picker';
import { QuantityStepper } from './quantity-stepper';

const COMMISSION_MAX_LENGTH = 30;

function draftToDelivery(
  d: { desiredDeliveryDate: string | null; desiredDeliveryWeek: string | null } | null,
): DeliveryValue {
  if (d?.desiredDeliveryDate) return { mode: 'date', date: d.desiredDeliveryDate.slice(0, 10) };
  if (d?.desiredDeliveryWeek) return { mode: 'week', week: d.desiredDeliveryWeek };
  return { mode: 'none' };
}

function formatDeliverySummary(d: DeliveryValue): string {
  if (d.mode === 'date' && d.date) return formatDate(d.date, 'dd.MM.yyyy');
  if (d.mode === 'week' && d.week) {
    const [, w] = d.week.split('-W');
    return `KW ${w}`;
  }
  return '';
}

export function CurrentRequestView() {
  const {
    items,
    itemCount,
    loading,
    justSubmittedNav,
    dismissNavBanner,
    updateQuantity,
    updateItemNote,
    removeItem,
    submit,
    currentDraft,
    updateHeader,
  } = useCart();

  const navBanner = justSubmittedNav ? (
    <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex items-start gap-3 shadow-[0_2px_4px_rgba(44,44,43,0.05)]">
      <CheckCircle2 size={20} className="text-emerald-600 shrink-0 mt-0.5" aria-hidden="true" />
      <div className="text-sm flex-1">
        <p className="font-semibold text-emerald-900">Anfrage wurde gesendet</p>
        {justSubmittedNav.navImportedAt && (
          <p className="text-emerald-700 mt-0.5">
            am {formatDate(justSubmittedNav.navImportedAt, 'dd.MM.yyyy HH:mm')} Uhr
          </p>
        )}
      </div>
      <button
        type="button"
        onClick={dismissNavBanner}
        className="p-1 -m-1 text-emerald-600 hover:text-emerald-800 hover:bg-emerald-100 rounded transition-colors shrink-0"
        aria-label="Hinweis ausblenden"
      >
        <X size={18} aria-hidden="true" />
      </button>
    </div>
  ) : null;

  const [commission, setCommission] = useDebouncedInput(
    currentDraft?.commission ?? '',
    (v) => updateHeader({ commission: v }),
    500,
  );
  const [generalNote, setGeneralNote] = useDebouncedInput(
    currentDraft?.note ?? '',
    (v) => updateHeader({ note: v }),
    500,
  );

  const [delivery, setDelivery] = useState<DeliveryValue>({ mode: 'none' });
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [openNotes, setOpenNotes] = useState<Record<string, boolean>>({});
  const toggleNote = useCallback(
    (articleNumber: string) =>
      setOpenNotes((prev) => ({ ...prev, [articleNumber]: !prev[articleNumber] })),
    [],
  );
  const seededDraftId = useRef<string | null>(null);
  useEffect(() => {
    const id = currentDraft?.id ?? null;
    if (!id || seededDraftId.current === id) return;
    seededDraftId.current = id;
    setDelivery(draftToDelivery(currentDraft));
  }, [currentDraft]);

  const handleDeliveryChange = useCallback(
    (v: DeliveryValue) => {
      setDelivery(v);
      if (v.mode === 'date' && !v.date) return;
      updateHeader({
        desiredDeliveryDate: v.mode === 'date' ? v.date : null,
        desiredDeliveryWeek: v.mode === 'week' ? v.week : null,
      });
    },
    [updateHeader],
  );

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = useCallback(async () => {
    setIsSubmitting(true);
    try {
      const dateArg = delivery.mode === 'date' && delivery.date ? delivery.date : undefined;
      const weekArg = delivery.mode === 'week' && delivery.week ? delivery.week : undefined;
      await submit(commission || undefined, dateArg, weekArg, generalNote || undefined);
      setCommission('');
      setDelivery({ mode: 'none' });
      setGeneralNote('');
      setOpenNotes({});
    } finally {
      setIsSubmitting(false);
    }
  }, [commission, delivery, generalNote, submit, setCommission, setGeneralNote]);

  if (loading && itemCount === 0) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Spinner size="lg" />
      </div>
    );
  }

  if (itemCount === 0) {
    return (
      <div className="space-y-6">
        {navBanner}
        <div className="max-w-2xl mx-auto text-center py-20">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-zinc-100 mb-6">
            <ClipboardList size={36} className="text-zinc-400" />
          </div>
          <h2 className="text-2xl font-bold text-secondary mb-3">Noch keine Artikel in der Anfrage</h2>
          <p className="text-text-muted mb-8">
            Fügen Sie Artikel aus der Artikelsuche oder Ihren Favoriten hinzu. Ihr Sachbearbeiter erhält die Anfrage und erstellt Ihnen ein Angebot.
          </p>
          <div className="flex items-center justify-center gap-4">
            <Link href="/artikelsuche" className="inline-flex items-center gap-2 px-5 py-3 bg-primary text-white rounded-xl font-bold hover:bg-primary-hover transition-colors">
              <PackageSearch size={18} />
              Artikelsuche
            </Link>
            <Link href="/favoriten" className="inline-flex items-center gap-2 px-5 py-3 border border-zinc-300 text-zinc-700 rounded-xl font-bold hover:bg-zinc-50 transition-colors">
              <Heart size={18} />
              Favoriten
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {navBanner}

      {/* Anfrage-Details — einklappbar */}
      <div className="bg-white rounded-xl border border-zinc-200 shadow-[0_2px_4px_rgba(44,44,43,0.05)] overflow-hidden">
        <button
          type="button"
          onClick={() => setDetailsOpen((o) => !o)}
          aria-expanded={detailsOpen}
          className="w-full flex items-center gap-3 p-4 text-left hover:bg-zinc-50 transition-colors"
        >
          <span className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-orange-50 text-primary shrink-0">
            <ClipboardList size={18} />
          </span>
          <span className="flex-1 min-w-0">
            <span className="flex items-center gap-2 font-bold text-secondary">
              Anfrage-Details
              <span className="text-[10px] font-semibold uppercase tracking-wider text-zinc-400 border border-zinc-200 rounded px-1.5 py-0.5">
                optional
              </span>
            </span>
            <span className="block text-sm text-zinc-500 mt-0.5">
              {detailsOpen ? 'Wird so an Ihren Sachbearbeiter übergeben' : 'Kommission · Liefertermin · Nachricht'}
            </span>
          </span>
          <ChevronRight size={18} aria-hidden="true" className={clsx('text-zinc-400 transition-transform shrink-0', detailsOpen && 'rotate-90')} />
        </button>

        {!detailsOpen && (
          <div className="divide-y divide-zinc-100 border-t border-zinc-100">
            <SummaryRow icon={<Building2 size={18} aria-hidden="true" />} label="Kommission / Bauvorhaben" value={commission} empty="Noch nicht angegeben" onClick={() => setDetailsOpen(true)} />
            <SummaryRow icon={<Calendar size={18} aria-hidden="true" />} label="Wunsch-Liefertermin" value={formatDeliverySummary(delivery)} empty="Keine Angabe" onClick={() => setDetailsOpen(true)} />
            <SummaryRow icon={<Mail size={18} aria-hidden="true" />} label="Nachricht an Sachbearbeiter" value={generalNote.trim()} empty="Keine Nachricht" onClick={() => setDetailsOpen(true)} />
          </div>
        )}

        {detailsOpen && (
          <div className="border-t border-zinc-100 p-4 space-y-5">
            <div>
              <label className="block text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-2">
                Kommission / Bauvorhaben
              </label>
              <input
                type="text"
                value={commission}
                onChange={(e) => setCommission(e.target.value)}
                onBlur={() => updateHeader({ commission })}
                maxLength={COMMISSION_MAX_LENGTH}
                placeholder="z. B. Neubau Müller, Sanierung Dachstuhl"
                className="w-full px-4 py-2.5 border border-zinc-300 rounded-lg text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
              />
              <div className="mt-1 flex justify-between text-xs text-zinc-400">
                <span>Wird so an Ihren Sachbearbeiter übergeben</span>
                <span className={commission.length >= COMMISSION_MAX_LENGTH ? 'text-amber-600 font-semibold' : ''}>
                  {commission.length}/{COMMISSION_MAX_LENGTH}
                </span>
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-2">
                Wunsch-Liefertermin
              </label>
              <DeliveryDatePicker value={delivery} onChange={handleDeliveryChange} />
            </div>
            <div>
              <label className="block text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-2">
                Allgemeine Nachricht an Sachbearbeiter
              </label>
              <textarea
                value={generalNote}
                onChange={(e) => setGeneralNote(e.target.value)}
                onBlur={() => updateHeader({ note: generalNote })}
                placeholder="Nachricht eingeben..."
                rows={3}
                className="w-full px-4 py-2.5 border border-zinc-300 rounded-lg text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none resize-y"
              />
            </div>
            <button
              type="button"
              onClick={() => setDetailsOpen(false)}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-bold text-primary hover:bg-orange-50 rounded-lg transition-colors"
            >
              <Check size={15} aria-hidden="true" />
              Fertig
            </button>
          </div>
        )}
      </div>

      {/* Positionen */}
      <div className="bg-white rounded-xl border border-zinc-200 shadow-[0_2px_4px_rgba(44,44,43,0.05)] overflow-hidden">
        {/* Desktop-Tabelle (unverändert) */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-zinc-50 border-b border-zinc-200">
                <th className="px-5 py-3 text-left text-xs font-semibold text-zinc-500 uppercase tracking-wider">Artikel</th>
                <th className="px-5 py-3 text-center text-xs font-semibold text-zinc-500 uppercase tracking-wider">Anzahl</th>
                <th className="px-5 py-3 text-right text-xs font-semibold text-zinc-500 uppercase tracking-wider">Menge Einheit</th>
                <th className="px-5 py-3 text-left text-xs font-semibold text-zinc-500 uppercase tracking-wider">Notiz</th>
                <th className="px-5 py-3 w-12"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {items.map((item) => {
                const breakdown = formatRequestPackageBreakdown(item);
                const anzahl = getRequestAnzahl(item);
                const setAnzahl = (n: number) => updateQuantity(item.articleNumber, Math.max(0, n));
                return (
                  <tr key={item.articleNumber} className="group hover:bg-zinc-50 transition-colors">
                    <td className="px-5 py-4">
                      <ArticleNameBlock
                        articleNumber={item.articleNumber}
                        description={item.description || item.articleNumber}
                        description2={item.description2}
                        descriptionClassName="font-semibold text-secondary"
                        description2ClassName="text-sm text-zinc-600"
                        articleNumberClassName="text-xs text-zinc-500 font-mono"
                      />
                    </td>
                    <td className="px-5 py-4">
                      <QuantityStepper
                        value={anzahl}
                        onChange={setAnzahl}
                        containerClassName="flex items-center justify-center gap-1"
                        buttonClassName="w-8 h-8 border border-zinc-300 rounded-md hover:bg-zinc-100 flex items-center justify-center transition-colors"
                        inputClassName="w-14 text-center font-bold text-secondary text-base border border-zinc-300 rounded-md outline-none tabular-nums py-1"
                      />
                    </td>
                    <td className="px-5 py-4 text-right whitespace-nowrap">
                      <p className="font-semibold text-secondary tabular-nums">
                        {formatQuantity(item.quantity)} <span className="text-zinc-600 font-normal">{item.unit}</span>
                      </p>
                      {breakdown && <p className="text-xs text-zinc-500 tabular-nums mt-0.5">{breakdown}</p>}
                    </td>
                    <td className="px-5 py-4">
                      <input
                        type="text"
                        defaultValue={item.note ?? ''}
                        onBlur={(e) => updateItemNote(item.articleNumber, e.target.value)}
                        placeholder="Notiz hinzufügen..."
                        className="w-full px-3 py-1.5 text-sm border border-zinc-200 rounded-md focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
                      />
                    </td>
                    <td className="px-5 py-4">
                      <button
                        onClick={() => removeItem(item.articleNumber)}
                        className="p-1.5 text-zinc-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors opacity-0 group-hover:opacity-100"
                        title="Entfernen"
                      >
                        <Trash2 size={16} aria-hidden="true" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Mobile-Karten: Bild + Notiz-Toggle */}
        <div className="md:hidden divide-y divide-zinc-100">
          {items.map((item) => {
            const breakdown = formatRequestPackageBreakdown(item);
            const anzahl = getRequestAnzahl(item);
            const setAnzahl = (n: number) => updateQuantity(item.articleNumber, Math.max(0, n));
            const hasNote = !!(item.note && item.note.trim());
            const noteOpen = openNotes[item.articleNumber] ?? hasNote;
            return (
              <div key={item.articleNumber} className="p-4 space-y-3">
                <div className="flex gap-3 items-start">
                  <ProductImage
                    src={item.imageUrl}
                    alt={item.description || item.articleNumber}
                    size="md"
                    className="bg-white border-zinc-200 shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <ArticleNameBlock
                      articleNumber={item.articleNumber}
                      description={item.description || item.articleNumber}
                      description2={item.description2}
                      descriptionClassName="font-semibold text-secondary"
                      description2ClassName="text-sm text-zinc-600"
                      articleNumberClassName="text-xs text-zinc-500 font-mono"
                    />
                  </div>
                  <button onClick={() => removeItem(item.articleNumber)} className="p-1.5 text-zinc-400 hover:text-red-500 shrink-0" title="Position entfernen">
                    <Trash2 size={16} aria-hidden="true" />
                  </button>
                </div>

                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <QuantityStepper
                      value={anzahl}
                      onChange={setAnzahl}
                      containerClassName="flex items-center gap-1"
                      buttonClassName="w-8 h-8 border border-zinc-300 rounded-md flex items-center justify-center"
                      inputClassName="w-14 text-center font-bold text-secondary border border-zinc-300 rounded-md tabular-nums py-1"
                    />
                    <div className="leading-tight">
                      <span className="font-semibold text-secondary tabular-nums whitespace-nowrap block">
                        {formatQuantity(item.quantity)} <span className="text-zinc-600 font-normal">{item.unit}</span>
                      </span>
                      {breakdown && <span className="text-xs text-zinc-500 tabular-nums">{breakdown}</span>}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => toggleNote(item.articleNumber)}
                    aria-expanded={noteOpen}
                    className={clsx(
                      'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-bold transition-colors',
                      hasNote ? 'text-primary bg-orange-50 hover:bg-orange-100' : 'text-primary hover:bg-orange-50',
                    )}
                  >
                    {hasNote ? <Check size={14} aria-hidden="true" /> : <Plus size={14} aria-hidden="true" />}
                    Notiz
                  </button>
                </div>

                {noteOpen && (
                  <textarea
                    defaultValue={item.note ?? ''}
                    onBlur={(e) => updateItemNote(item.articleNumber, e.target.value)}
                    placeholder="Notiz zu dieser Position…"
                    rows={2}
                    className="w-full px-3 py-2 text-sm border border-zinc-200 rounded-md focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none resize-y"
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Footer / Submit — Desktop: normale Card */}
      <div className="hidden md:flex bg-white rounded-xl border border-zinc-200 p-6 shadow-[0_2px_4px_rgba(44,44,43,0.05)] justify-end items-center gap-4">
        <button
          onClick={handleSubmit}
          disabled={isSubmitting || itemCount === 0}
          className="px-6 py-3 bg-primary text-white rounded-xl font-bold hover:bg-primary-hover shadow-lg shadow-orange-200 transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSubmitting ? <Spinner size="sm" className="text-white" /> : (<><Send size={18} aria-hidden="true" />Anfrage absenden</>)}
        </button>
      </div>

      {/* Spacer, damit die letzte Position nicht hinter der fixed Bar liegt (nur Mobile) */}
      <div className="h-24 md:hidden" aria-hidden="true" />

      {/* Submit — Mobile: fixed über der Bottom-Nav (h-16 = 4rem) */}
      <div className="md:hidden fixed inset-x-0 bottom-[calc(4rem+env(safe-area-inset-bottom))] z-40 px-4 pt-2 pb-3 bg-gradient-to-t from-white via-white to-transparent">
        <button
          onClick={handleSubmit}
          disabled={isSubmitting || itemCount === 0}
          className="w-full px-6 py-3.5 bg-primary text-white rounded-xl font-bold hover:bg-primary-hover shadow-lg shadow-orange-200 transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSubmitting ? <Spinner size="sm" className="text-white" /> : (<><Send size={18} aria-hidden="true" />Anfrage absenden</>)}
        </button>
      </div>
    </div>
  );
}

function SummaryRow({
  icon,
  label,
  value,
  empty,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  empty: string;
  onClick: () => void;
}) {
  const filled = value.trim().length > 0;
  return (
    <button type="button" onClick={onClick} className="w-full flex items-center gap-3 p-4 text-left hover:bg-zinc-50 transition-colors">
      <span className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-zinc-100 text-zinc-500 shrink-0">{icon}</span>
      <span className="flex-1 min-w-0">
        <span className="block text-[11px] font-semibold uppercase tracking-wider text-zinc-400">{label}</span>
        <span className={clsx('block truncate', filled ? 'font-semibold text-secondary' : 'text-zinc-400 italic')}>
          {filled ? value : empty}
        </span>
      </span>
      {filled ? (
        <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-emerald-50 text-emerald-600 shrink-0">
          <Check size={13} aria-hidden="true" />
        </span>
      ) : (
        <ChevronRight size={16} aria-hidden="true" className="text-zinc-400 shrink-0" />
      )}
    </button>
  );
}
